import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Pin, PinOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { useToast } from "@/hooks/use-toast";
import { CURRENCY_SYMBOLS } from "@/lib/currencyConversion";
import { useLanguage } from "@/contexts/LanguageContext";

interface QuickExpense {
  category: string;
  merchant: string;
  amount: number;
  currency: string;
}

interface QuickAction extends QuickExpense {
  id?: string;
  isPinned: boolean;
  displayName: string;
}

interface QuickActionsProps {
  userId: string;
  onQuickAdd: (expense: QuickExpense) => Promise<void>;
}

const getDefaultSuggestions = (currency: string): QuickExpense[] => [
  { category: 'Coffee', merchant: 'Coffee', amount: 3, currency },
  { category: 'Food', merchant: 'Lunch', amount: 10, currency },
  { category: 'Transport', merchant: 'Transport', amount: 5, currency },
  { category: 'Fun', merchant: 'Entertainment', amount: 15, currency },
];

export const QuickActions = ({ userId, onQuickAdd }: QuickActionsProps) => {
  const { t } = useLanguage();
  const [actions, setActions] = useState<QuickAction[]>([]);
  const [addedChip, setAddedChip] = useState<string | null>(null);
  const [longPressAction, setLongPressAction] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userCurrency, setUserCurrency] = useState<string | null>(null);
  const { light, success: hapticSuccess } = useHapticFeedback();
  const { toast } = useToast();

  useEffect(() => {
    loadUserCurrency();
  }, [userId]);

  useEffect(() => {
    if (userCurrency) {
      loadActions();
    }
  }, [userId, userCurrency]);

  const loadUserCurrency = async () => {
    try {
      const { data } = await supabase
        .from('budget_settings')
        .select('currency')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (data?.currency) {
        setUserCurrency(data.currency);
      }
    } catch (error) {
      console.error('Error loading user currency:', error);
    }
  };

  const loadActions = async () => {
    try {
      // Load pinned templates
      const { data: templates, error: templatesError } = await supabase
        .from('expense_templates')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;

      const pinnedActions: QuickAction[] = (templates || []).map(t => ({
        id: t.id,
        category: t.category,
        merchant: t.merchant,
        amount: t.amount,
        currency: userCurrency || t.currency, // Always display in user's currency
        isPinned: true,
        displayName: t.name,
      }));

      // Load smart suggestions from cache or calculate
      let smartSuggestions: QuickAction[] = [];
      
      const cached = localStorage.getItem(`quickActions_${userId}`);
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      
      if (cached) {
        const { suggestions: cachedSuggestions, lastUpdated } = JSON.parse(cached);
        if (Date.now() - lastUpdated < oneWeek) {
          smartSuggestions = cachedSuggestions.map((s: QuickExpense) => ({
            ...s,
            isPinned: false,
            displayName: s.merchant,
          }));
        }
      }

      // If no cache or expired, calculate from expenses
      if (smartSuggestions.length === 0) {
        const { data: expenses, error: expensesError } = await supabase
          .from('expenses')
          .select('category, merchant, amount, currency')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(100);

        if (expensesError) throw expensesError;

        if (!expenses || expenses.length < 5) {
          smartSuggestions = getDefaultSuggestions(userCurrency).map(s => ({
            ...s,
            isPinned: false,
            displayName: s.merchant,
          }));
        } else {
          const frequencyMap = new Map<string, { count: number; expense: QuickExpense }>();
          
          expenses.forEach(expense => {
            const key = `${expense.category}-${expense.merchant}-${expense.amount}`;
            const existing = frequencyMap.get(key);
            
            if (existing) {
              existing.count++;
            } else {
              frequencyMap.set(key, {
                count: 1,
                expense: {
                  category: expense.category,
                  merchant: expense.merchant,
                  amount: expense.amount,
                  currency: expense.currency,
                }
              });
            }
          });

          smartSuggestions = Array.from(frequencyMap.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 6)
            .map(item => ({
              ...item.expense,
              isPinned: false,
              displayName: item.expense.merchant,
            }));

          // Cache results
          localStorage.setItem(`quickActions_${userId}`, JSON.stringify({
            suggestions: smartSuggestions.map(({ isPinned, displayName, ...rest }) => rest),
            lastUpdated: Date.now(),
          }));
        }
      }

      // De-duplicate: remove smart suggestions that match pinned items
      const pinnedKeys = new Set(
        pinnedActions.map(a => `${a.category}-${a.merchant}-${a.amount}`)
      );
      
      const uniqueSuggestions = smartSuggestions.filter(s => {
        const key = `${s.category}-${s.merchant}-${s.amount}`;
        return !pinnedKeys.has(key);
      });

      // Combine: pinned first, then suggestions (max 8-10 total)
      const combined = [...pinnedActions, ...uniqueSuggestions].slice(0, 10);
      setActions(combined);
      
    } catch (error) {
      console.error('Error loading quick actions:', error);
      setActions(getDefaultSuggestions(userCurrency).map(s => ({
        ...s,
        isPinned: false,
        displayName: s.merchant,
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleChipClick = async (action: QuickAction, actionKey: string) => {
    if (longPressAction === actionKey) return;
    
    setAddedChip(actionKey);
    hapticSuccess();
    
    try {
      await onQuickAdd({
        category: action.category,
        merchant: action.merchant,
        amount: action.amount,
        currency: action.currency,
      });
    } catch (error) {
      console.error('Quick add failed:', error);
    }
    
    setTimeout(() => {
      setAddedChip(null);
    }, 1000);
  };

  const handlePin = async (action: QuickAction, actionKey: string) => {
    light();
    
    if (action.isPinned && action.id) {
      // Unpin: delete template
      try {
        await supabase
          .from('expense_templates')
          .delete()
          .eq('id', action.id);
        
        toast({
          title: t('quickAdd.unpinned'),
          description: t('quickAdd.unpinnedDesc', { name: action.displayName }),
          duration: 2000,
        });
        
        loadActions(); // Refresh
      } catch (error) {
        console.error('Error unpinning:', error);
      }
    } else {
      // Pin: create template
      try {
        await supabase
          .from('expense_templates')
          .insert({
            user_id: userId,
            name: action.displayName,
            amount: action.amount,
            merchant: action.merchant,
            category: action.category,
            currency: action.currency,
          });
        
        toast({
          title: t('quickAdd.pinned'),
          description: t('quickAdd.pinnedDesc', { name: action.displayName }),
          duration: 2000,
        });
        
        loadActions(); // Refresh
      } catch (error) {
        console.error('Error pinning:', error);
      }
    }
    
    setLongPressAction(null);
  };

  const handleLongPress = (actionKey: string) => {
    light();
    setLongPressAction(actionKey);
  };

  if (loading || actions.length === 0 || !userCurrency) return null;

  return (
    <div className="relative z-40 space-y-2 pointer-events-auto">
      <p className="text-sm text-muted-foreground">{t('quickAdd.actions')}</p>
      <div 
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide touch-pan-x"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {actions.map((action, index) => {
          const actionKey = action.id || `${action.category}-${action.merchant}-${action.amount}-${index}`;
          const icon = getCategoryIcon(action.category);
          const isAdded = addedChip === actionKey;
          const isShowingMenu = longPressAction === actionKey;
          
          return (
            <div key={actionKey} className="relative flex-shrink-0 pointer-events-auto">
              <motion.button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChipClick(action, actionKey);
                }}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLongPress(actionKey);
                }}
                onTouchStart={(e) => {
                  const timeout = setTimeout(() => {
                    handleLongPress(actionKey);
                  }, 500);
                  (e.currentTarget as any).longPressTimeout = timeout;
                }}
                onTouchEnd={(e) => {
                  const timeout = (e.currentTarget as any).longPressTimeout;
                  if (timeout) clearTimeout(timeout);
                }}
                onTouchMove={(e) => {
                  const timeout = (e.currentTarget as any).longPressTimeout;
                  if (timeout) clearTimeout(timeout);
                }}
                className={`relative px-4 py-2.5 rounded-full transition-all text-sm whitespace-nowrap cursor-pointer select-none min-h-[48px] min-w-[48px] active:scale-95 ${
                  action.isPinned
                    ? 'bg-primary/10 hover:bg-primary/20 border-2 border-primary/30'
                    : 'bg-muted hover:bg-muted/80 border border-border'
                }`}
                style={{ 
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
                whileTap={{ scale: 0.95 }}
                disabled={isAdded}
              >
                <span className="flex items-center gap-1.5">
                  {action.isPinned ? <span>⭐</span> : <span>{icon}</span>}
                  <span>{action.displayName}</span>
                  <span className="font-medium">
                    {CURRENCY_SYMBOLS[action.currency as keyof typeof CURRENCY_SYMBOLS] || action.currency}{action.amount}
                  </span>
                </span>
                
                {isAdded && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-green-500 rounded-full"
                  >
                    <Check className="w-4 h-4 text-white" />
                  </motion.div>
                )}
              </motion.button>

              {/* Pin/Unpin Menu */}
              <AnimatePresence>
                {isShowingMenu && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => setLongPressAction(null)}
                    />
                    <motion.button
                      initial={{ scale: 0, y: -10 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0, y: -10 }}
                      onClick={() => handlePin(action, actionKey)}
                      className="absolute -top-12 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-popover border border-border rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                    >
                      {action.isPinned ? (
                        <>
                          <PinOff className="w-4 h-4" />
                          {t('quickAdd.unpin')}
                        </>
                      ) : (
                        <>
                          <Pin className="w-4 h-4" />
                          {t('quickAdd.pin')}
                        </>
                      )}
                    </motion.button>
                  </>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
